<template>
  <div class="page-customers">
    <nav class="breadcrumb mt-5" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li class="is-active"><router-link to="/dashboard/customers" aria-current="true">Customers</router-link></li>
      </ul>
    </nav>

    <div class="columns is-multiline">
      <div class="column is-12">
        <h1 class="title">Customers</h1>
        <button class="button is-primary" @click="openNewCustomerQuickview">New Customer</button>
      </div>

      <div class="column is-12 tabs is-toggle is-small mt-4">
        <ul>
          <li :class="{ 'is-active': selectedTab === 'All' }" @click="selectedTab = 'All'">
            <a>All</a>
          </li>
          <li :class="{ 'is-active': selectedTab === 'BRN' }" @click="selectedTab = 'BRN'">
            <a>BRN</a>
          </li>
          <li :class="{ 'is-active': selectedTab === 'NRIC' }" @click="selectedTab = 'NRIC'">
            <a>NRIC</a>
          </li>
          <li :class="{ 'is-active': selectedTab === 'NA' }" @click="selectedTab = 'NA'">
            <a>Consolidated</a>
          </li>
        </ul>
      </div>

      <div class="columns" style="gap: 15px; margin-top: 10px; padding-left: 20px">
        <input v-model="filters.buyerName" type="text" class="input is-rounded is-small" placeholder="Buyer Name">
        <input v-model="filters.identityNumber" type="text" class="input is-rounded is-small" placeholder="Identity Number">

        <button class="button is-primary is-small" @click="applyFilters">Apply Filters</button>
        <button class="button is-light is-small" @click="clearFilters">Clear Filters</button>

        <button class="button is-info is-rounded is-small" @click="downloadCSV">CSV Report</button>
        <div class="select is-link is-small">
          <select v-model="itemsPerPage">
            <option value="10">10</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>


      <div v-if="loading" class="has-text-centered">
        <span class="is-size-6 has-text-grey">Loading...</span>
      </div>

      <div class="column is-12">
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th @click="sortBy('name')" class="is-size-7">Customer Name <button :class="getSortClass('name')"></button></th>
              <th @click="sortBy('tin_no')" class="is-size-7">TIN No <button :class="getSortClass('tin_no')"></button></th>
              <th @click="sortBy('identity_number')" class="is-size-7">Identity No <button :class="getSortClass('identity_number')"></button></th>
              <th @click="sortBy('identity_type')" class="is-size-7">Identity Type <button :class="getSortClass('identity_type')"></button></th>
              <th @click="sortBy('email')" class="is-size-7">Email <button :class="getSortClass('email')"></button></th>
              <th @click="sortBy('city')" class="is-size-7">City <button :class="getSortClass('city')"></button></th>
              <th @click="sortBy('contact_number')" class="is-size-7">Contact Number <button :class="getSortClass('contact_number')"></button></th>
              <th class="is-size-7">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="customer in customers" :key="customer.id" class="customer-row">
              <td style="text-align: left" class="is-size-7">{{ customer.name }}</td>
              <td class="is-size-7">{{ customer.tin_no }}</td>
              <td class="is-size-7">{{ customer.identity_number }}</td>
              <td style="text-align: center" class="is-size-7">{{ customer.identity_type }}</td>
              <td class="is-size-7">{{ customer.email }}</td>
              <td class="is-size-7">{{ customer.city }}</td>
              <td style="text-align: left" class="is-size-7">{{ customer.contact_number }}</td>
              <td>
                <button class="button is-small is-info" @click="editCustomer(customer)">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination">
          <button @click="prevPage" :disabled="currentPage === 1" class="button is-ghost is-small">Previous</button>
          <span class="has-text-grey is-size-7">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }}–
            {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} results
          </span>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="button is-ghost is-small">Next</button>
        </div>
      </div>
    </div>

    <AddCustomer 
      v-if="showQuickview" 
      :customer="newCustomer" 
      @close="closeQuickview" 
      @customerSaved="getCustomers"
      ref="AddCustomer" 
    />
  </div>
</template>

<script>
import axios from 'axios';
import AddCustomer from '@/components/AddCustomer.vue';

export default {
  name: 'ViewCustomers',
  components: {
    AddCustomer,
  },
  data() {
    return {
      customers: [],
      newCustomer: {
        name: '',
        entity_code: '',
        tin_no: '',
        identity_type: '',
        identity_number: '',
        email: '',
        city: '',
        contact_number: '',
        source_ui: true,
      },
      showQuickview: false,
      filters: {
        buyerName: '',
        identityType: '',
        identityNumber: ''
      },
      currentPage: 1,
      itemsPerPage: 10,
      selectedTab: 'All',
      totalItems: 0, // ✅ Needed for pagination
      loading: false, // ✅ Needed for loading state
      sortKey: 'name',
      sortOrders: {
        name: 1,
        tin_no: 1,
        identity_number: 1,
        identity_type: 1,
        email: 1,
        city: 1,
        contact_number: 1,
      },

    };
  },
  mounted() {
    this.getCustomers();
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
  },
  methods: {
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrders[key] = this.sortOrders[key] * -1;
      } else {
        this.sortKey = key;
        if (!this.sortOrders[key]) {
          this.sortOrders[key] = 1;
        }
      }
      this.getCustomers();
    },
    getSortClass(key) {
      if (this.sortKey !== key) return '';
      return this.sortOrders[key] === 1 ? 'sort-asc' : 'sort-desc';
    },


    clearFilters() {
      this.filters.buyerName = '';
      this.filters.identityNumber = '';
      this.selectedTab = 'All';
      this.currentPage = 1;
      this.getCustomers();
    },
    applyFilters() {
      this.currentPage = 1;
      this.getCustomers();
    },  
    async getCustomers() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          page_size: this.itemsPerPage,
          sort_by: this.sortKey || 'name',
          order: this.sortOrders[this.sortKey] === -1 ? 'desc' : 'asc',
          identity_number: this.filters.identityNumber || undefined,
          name: this.filters.buyerName || undefined,
          identity_type: this.selectedTab !== 'All' ? this.selectedTab : undefined,
        };

        const response = await axios.get('/api/v1/customers/', { params });
        console.log("Fetched customers:", response.data.results);
        this.customers = response.data.results;
        this.totalItems = response.data.count;
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        this.loading = false;
      }
    },

    downloadCSV() {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "Name,Entity Code,TIN No,Identity Type,Identity Number,Email,City,Contact Number\n"; // CSV Header
      this.customers.forEach(customer => {
        let row = `${customer.name},${customer.entity_code},${customer.tin_no},${customer.identity_type},${customer.identity_number},${customer.email},${customer.city},${customer.contact_number}`;
        csvContent += row + "\n";
      });

      const date = new Date();
      const dateString = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`;

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `customers_${dateString}.csv`);
      document.body.appendChild(link); // Required for FF
      link.click();
    },
    openNewCustomerQuickview() {
      this.newCustomer = {
        name: '',
        entity_code: '',
        tin_no: '',
        identity_type: '',
        identity_number: '',
        email: '',
        city: '',
        contact_number: '',
        source_ui: true,
      };
      this.showQuickview = true;
    },
    editCustomer(customer) {
      this.newCustomer = { ...customer };
      this.showQuickview = true;
    },
    closeQuickview() {
      this.showQuickview = false;
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getCustomers();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getCustomers();
      }
    },
  },
  watch: {
    itemsPerPage(val) {
      console.log('Page size changed to', val);
      this.currentPage = 1;
      this.getCustomers();
    },
    selectedTab() {
      this.currentPage = 1;
      this.getCustomers();
    },
  },

};
</script>

<style scoped>
.customer-row {
  cursor: pointer;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
}
.pagination button {
  margin: 0 0.5rem;
}
.sort-asc::after {
  content: ' ▲';
}
.sort-desc::after {
  content: ' ▼';
}
</style>
